import React from 'react';
import SectionPapersTable from './SectionPapersTable';
import { sectionPapersOfTier } from '../utils/sectionPapers';
import {
  PRACTICE_TOPICS_ALL_PAGE,
  PRACTICE_TOPICS_LABEL,
  PRACTICE_TOPIC_PAGE_TITLES,
  PRACTICE_TOPIC_TIER_PAGES,
} from '../utils/practiceTopics';

/**
 * The Practice Topics pages of an exam's workspace: Beginnings, Foundations, Beyond, and
 * the page with all three. Renders nothing on any other page, so a workspace mounts it
 * once beside its other pages.
 *
 * `topics` is what the workspace's Practice Topics page shows: a node (it is the
 * Beginnings page, with any section paper filed under beginnings after it) or a function
 * of the tier that returns the topic papers of that tier, or null. Foundations and Beyond list the
 * section papers of their tier. `prefix` is the workspace's CSS prefix.
 */
const PracticeTopicsPages = ({
  page,
  prefix,
  exam,
  topics = null,
  sectionPapers = [],
  papers = null,
  label = PRACTICE_TOPICS_LABEL,
  allPage = PRACTICE_TOPICS_ALL_PAGE,
  tierPages = PRACTICE_TOPIC_TIER_PAGES,
  titles = PRACTICE_TOPIC_PAGE_TITLES,
  ofTier = sectionPapersOfTier,
  completedList = [],
  isPartUnlocked,
  onStartSetPaper,
  onRequestUpgrade,
}) => {
  const listed = papers || sectionPapers;
  const tierPage = tierPages.find((entry) => entry.page === page) || null;
  if (!tierPage && page !== allPage) return null;

  const papersTable = (papers) => (
    <SectionPapersTable
      exam={exam}
      prefix={prefix}
      papers={papers}
      completedList={completedList}
      isPartUnlocked={isPartUnlocked}
      onStartSetPaper={onStartSetPaper}
      onRequestUpgrade={onRequestUpgrade}
    />
  );

  const empty = (label) => (
    <div className={`${prefix}-empty`}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
        <path d="M4 5.5A2.5 2.5 0 016.5 3H19a1 1 0 011 1v14.5a1 1 0 01-1 1H6.5A2.5 2.5 0 014 17V5.5z" stroke="currentColor" strokeWidth="1.4" />
        <line x1="8" y1="8" x2="16" y2="8" stroke="currentColor" strokeWidth="1.4" />
        <line x1="8" y1="12" x2="14" y2="12" stroke="currentColor" strokeWidth="1.4" />
      </svg>
      <p>
        {`No ${label} practice yet. Check back soon, or start with the Mock Tests and Past Papers already available.`}
      </p>
    </div>
  );

  // What one tier's page lists; null when it has nothing (the all-tiers page then leaves it out).
  const tierContent = ({ tier }) => {
    const papers = ofTier(listed, tier);
    if (typeof topics !== 'function') {
      if (tier === 'beginnings') {
        return (
          <>
            {topics}
            {papers.length > 0 && papersTable(papers)}
          </>
        );
      }
      return papers.length ? papersTable(papers) : null;
    }
    const topicContent = topics(tier);
    if (!topicContent && !papers.length) return null;
    return (
      <>
        {topicContent}
        {papers.length > 0 && papersTable(papers)}
      </>
    );
  };

  if (tierPage) {
    return (
      <section className={`${prefix}-page`}>
        <div className={`${prefix}-page-header`}>
          <h1 className={`${prefix}-page-title`}>{titles[tierPage.page]}</h1>
        </div>
        {tierContent(tierPage) || empty(tierPage.label)}
      </section>
    );
  }

  return (
    <section className={`${prefix}-page`}>
      <div className={`${prefix}-page-header`}>
        <h1 className={`${prefix}-page-title`}>{label}</h1>
      </div>
      {tierPages.map((entry) => {
        const content = tierContent(entry);
        if (!content) return null;
        return (
          <div key={entry.page} className={`${prefix}-practice-tier`}>
            <h2 className={`${prefix}-practice-tier-title`}>{entry.label}</h2>
            {content}
          </div>
        );
      })}
    </section>
  );
};

export default PracticeTopicsPages;
